import { useEffect, useState } from "react";
import { loadPdfDocument, renderPageToJpegBlob } from "@/tools/pdf/utils";
import { Loader2, Check, X } from "lucide-react";

export interface PdfPageThumbnail {
  pageNumber: number;
  url: string;
}

/**
 * Renders every page of a PDF as a small real thumbnail image (not a
 * placeholder icon), for tools where seeing the actual page content
 * before committing to an action genuinely matters -- rotating the wrong
 * page, or splitting at the wrong point, are exactly the kind of mistake
 * a real preview prevents.
 *
 * Deliberately capped and scaled down (scale=0.4) since this can run
 * against PDFs with many pages -- full-resolution renders for a preview
 * strip would be wasted work and a real memory/performance cost.
 */
export function usePdfPageThumbnails(file: File | null) {
  const [thumbnails, setThumbnails] = useState<PdfPageThumbnail[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!file) {
      setThumbnails([]);
      return;
    }
    let cancelled = false;
    const urls: string[] = [];

    (async () => {
      setLoading(true);
      try {
        const pdf = await loadPdfDocument(file);
        const results: PdfPageThumbnail[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const blob = await renderPageToJpegBlob(page, 0.4, 0.7);
          const url = URL.createObjectURL(blob);
          urls.push(url);
          results.push({ pageNumber: i, url });
        }
        if (!cancelled) setThumbnails(results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [file]);

  return { thumbnails, loading };
}

export function PdfThumbnailLoading() {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading page previews...
    </div>
  );
}

/** Accessible name of a page button, e.g. "Page 7, marked for removal". */
export function pageButtonLabel(pageNumber: number, on: boolean, remove: boolean): string {
  return on ? `Page ${pageNumber}, ${remove ? "marked for removal" : "selected"}` : `Page ${pageNumber}`;
}

/**
 * A grid of page thumbnails where each page can be picked. "keep" marks picked pages with a tick (Extract,
 * Split); "remove" dims them and marks them with a cross (Remove pages). One component, so every
 * page-picking tool looks and behaves the same: the page number, keyboard access and the selected state.
 */
export function PdfPageGrid({ thumbnails, selected, onToggle, mode = "keep", captions }: {
  thumbnails: PdfPageThumbnail[];
  selected: Set<number>;
  onToggle: (pageNumber: number) => void;
  mode?: "keep" | "remove";
  /** Optional one-line note under a page (for example why it was picked). */
  captions?: Record<number, string>;
}) {
  const remove = mode === "remove";
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
      {thumbnails.map((t) => {
        const on = selected.has(t.pageNumber);
        return (
          <button key={t.pageNumber} type="button" aria-pressed={on} aria-label={pageButtonLabel(t.pageNumber, on, remove)} onClick={() => onToggle(t.pageNumber)}
            className={`relative rounded-lg border-2 overflow-hidden transition-colors ${on ? (remove ? "border-red-500" : "border-primary") : "border-border"}`}>
            <img src={t.url} alt={`Page ${t.pageNumber}`} className={`w-full aspect-[3/4] object-contain bg-white ${on && remove ? "opacity-40" : ""}`} />
            <span className="absolute top-1 left-1 text-xs px-1.5 py-0.5 rounded bg-background/90 border">{t.pageNumber}</span>
            {captions?.[t.pageNumber] && <span className="absolute bottom-1 left-1 right-1 text-[10px] leading-tight px-1 py-0.5 rounded bg-background/90 border text-center" data-testid={`caption-${t.pageNumber}`}>{captions[t.pageNumber]}</span>}
            {on && (
              <span className={`absolute top-1 right-1 h-5 w-5 rounded-full flex items-center justify-center ${remove ? "bg-red-500 text-white" : "bg-primary text-primary-foreground"}`}>
                {remove ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
